const MAX_OUTPUT_SIZE = 1500;
const JPEG_QUALITY = 0.85;

function createImage(imageSrc) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Failed to load image'));
    image.src = imageSrc;
  });
}

function getRadianAngle(degreeValue) {
  return (degreeValue * Math.PI) / 180;
}

function rotateSize(width, height, rotation) {
  const rotRad = getRadianAngle(rotation);

  return {
    width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}

function getOutputSize(width, height) {
  const largestSide = Math.max(width, height);

  if (largestSide <= MAX_OUTPUT_SIZE) {
    return {
      width: Math.round(width),
      height: Math.round(height),
      scale: 1,
    };
  }

  const scale = MAX_OUTPUT_SIZE / largestSide;

  return {
    width: Math.round(width * scale),
    height: Math.round(height * scale),
    scale,
  };
}

export async function getCroppedImg(imageSrc, crop, rotation = 0, flip = { horizontal: false, vertical: false }) {
  if (!crop?.width || !crop?.height) {
    throw new Error('Invalid crop area');
  }

  const image = await createImage(imageSrc);
  const sourceWidth = image.naturalWidth || image.width;
  const sourceHeight = image.naturalHeight || image.height;
  const rotRad = getRadianAngle(rotation);
  const rotatedSize = rotateSize(sourceWidth, sourceHeight, rotation);
  const output = getOutputSize(crop.width, crop.height);

  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, output.width);
  canvas.height = Math.max(1, output.height);

  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Could not create image canvas context');
  }

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  ctx.save();
  ctx.scale(output.scale, output.scale);
  ctx.translate(-crop.x, -crop.y);
  ctx.translate(rotatedSize.width / 2, rotatedSize.height / 2);
  ctx.rotate(rotRad);
  ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
  ctx.translate(-sourceWidth / 2, -sourceHeight / 2);
  ctx.drawImage(image, 0, 0, sourceWidth, sourceHeight);
  ctx.restore();

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob || blob.size === 0) {
        reject(new Error('Failed to create cropped image blob'));
        return;
      }

      resolve(blob);
    }, 'image/jpeg', JPEG_QUALITY);
  });
}
